import { config } from '../config.ts';
import { createStorageTexture2D, createUniformBuffer, writeUniform } from '../gpu/buffers.ts';
import { formats } from '../gpu/formats.ts';
import { createComputePipeline } from '../gpu/pipelines.ts';
import hashWgsl from './hash.wgsl?raw';
import heightmapWgsl from './heightmap.wgsl?raw';

const wgsl = hashWgsl + '\n' + heightmapWgsl;

export interface HeightmapModule {
  readonly texture: GPUTexture;
  readonly view: GPUTextureView;
  destroy(): void;
}

export async function createHeightmap(
  device: GPUDevice,
  opts: { seed: number },
): Promise<HeightmapModule> {
  const size = config.heightmapSize;

  const texture = createStorageTexture2D(
    device,
    [size, size],
    formats.heightStorage,
    'heightmap',
  );
  const view = texture.createView({ label: 'heightmap.view' });

  // Params: u32 seed + 12 bytes pad → 16 bytes (one std140-friendly slot).
  const paramsBuffer = createUniformBuffer(device, 16, 'heightmap.params');
  writeUniform(device, paramsBuffer, new Uint32Array([opts.seed >>> 0, 0, 0, 0]));

  const pipeline = createComputePipeline(device, wgsl, 'main', 'auto', 'heightmap');

  const bindGroup = device.createBindGroup({
    label: 'heightmap.bindGroup',
    layout: pipeline.getBindGroupLayout(0),
    entries: [
      { binding: 0, resource: view },
      { binding: 1, resource: { buffer: paramsBuffer } },
    ],
  });

  const encoder = device.createCommandEncoder({ label: 'heightmap.encoder' });
  const pass = encoder.beginComputePass({ label: 'heightmap.pass' });
  pass.setPipeline(pipeline);
  pass.setBindGroup(0, bindGroup);
  const groups = Math.ceil(size / 8);
  pass.dispatchWorkgroups(groups, groups, 1);
  pass.end();
  device.queue.submit([encoder.finish()]);

  return {
    texture,
    view,
    destroy() {
      texture.destroy();
      paramsBuffer.destroy();
    },
  };
}
